import React from "react";
import {
  TextField,
  MenuItem,
  Box,
  Autocomplete,
  CircularProgress,
  Alert,
  AlertTitle,
  Stack,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

export function renderBondSubCategory(config, data, errors, handleAutocompleteChange) {
  if (!config.bondSubCategoryOptions) return null;
  return (
    <Autocomplete
      options={config.bondSubCategoryOptions}
      value={data.bondSubCategory || ""}
      onChange={(event, value) => handleAutocompleteChange(event, value, "bondSubCategory")}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Bond Sub Category"
          fullWidth
          variant="outlined"
          required
          error={!!errors.bondSubCategory}
          helperText={errors.bondSubCategory}
        />
      )}
    />
  );
}

export function renderCounty(
  config,
  data,
  errors,
  counties,
  loadingCounties,
  handleAutocompleteChange
) {
  if (!config.showCounty) return null;
  return (
    <Autocomplete
      options={counties}
      value={data.county || ""}
      onChange={(event, value) => handleAutocompleteChange(event, value, "county")}
      loading={loadingCounties}
      renderInput={(params) => (
        <TextField
          {...params}
          label="County"
          fullWidth
          variant="outlined"
          required
          error={!!errors.county}
          helperText={errors.county}
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {loadingCounties ? <CircularProgress size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
    />
  );
}

export function renderProbateCountyFields(config, data, handleChange) {
  if (!config.probateCountyConditionals) return null;
  const judgeSternLabel =
    config.judgeSternLabel ||
    "Has Judge Stearn's issued his order requiring bond yet?";
  return (
    <>
      {data.county === "Pasco" && (
        <TextField
          select
          label={judgeSternLabel}
          name="judgeStern"
          value={data.judgeStern || "Yes"}
          onChange={handleChange}
          fullWidth
          required
        >
          <MenuItem value="Yes">Yes</MenuItem>
          <MenuItem value="No">No</MenuItem>
        </TextField>
      )}
      {data.judgeStern === "No" && (
        <Stack sx={{ width: "100%", mb: 2 }} spacing={2}>
          <Alert severity="warning" icon={<WarningAmberIcon fontSize="inherit" />}>
            <AlertTitle>Warning</AlertTitle>
            The court order by <strong>Judge Stearn</strong> is a mandatory requirement to create
            this bond. Please try again once the order is issued.
          </Alert>
        </Stack>
      )}
      {data.county === "Pinellas" && (
        <TextField
          select
          label="Is this bond is for the purposes of opening a Wrongful Death Lawsuit?"
          name="wrongfulDeathLawsuit"
          value={data.wrongfulDeathLawsuit || "No"}
          onChange={handleChange}
          fullWidth
          required
        >
          <MenuItem value="Yes">Yes</MenuItem>
          <MenuItem value="No">No</MenuItem>
        </TextField>
      )}
    </>
  );
}

export function renderNameFields(
  config,
  data,
  errors,
  handleChange,
  handleAutocompleteChange,
  { showAliasesAka = false } = {}
) {
  const { nameFields } = config;
  if (nameFields.mode === "trustee") {
    return (
      <>
        <TextField
          label={nameFields.legalNameLabel}
          name="legalName"
          value={data.legalName || ""}
          onChange={handleChange}
          fullWidth
          required
          error={!!errors.legalName}
          helperText={errors.legalName}
        />
        <TextField
          label={nameFields.trustNameLabel}
          name="trustName"
          value={data.trustName || ""}
          onChange={handleChange}
          fullWidth
          required
          error={!!errors.trustName}
          helperText={errors.trustName}
          slotProps={{ htmlInput: { maxLength: 200 } }}
        />
      </>
    );
  }

  const maxLength = nameFields.maxLength || 200;
  const aliasesMaxLength = nameFields.aliasesMaxLength;
  return (
    <>
      <TextField
        label={nameFields.firstLabel}
        name="firstName"
        value={data.firstName || ""}
        onChange={handleChange}
        fullWidth
        required
        error={!!errors.firstName}
        helperText={errors.firstName}
        slotProps={{ htmlInput: { maxLength } }}
      />
      <TextField
        label={nameFields.middleLabel}
        name="middleName"
        value={data.middleName || ""}
        onChange={handleChange}
        fullWidth
        slotProps={{ htmlInput: { maxLength } }}
      />
      <TextField
        label={nameFields.lastLabel}
        name="lastName"
        value={data.lastName || ""}
        onChange={handleChange}
        fullWidth
        required
        error={!!errors.lastName}
        helperText={errors.lastName}
        slotProps={{ htmlInput: { maxLength } }}
      />
      {nameFields.showSuffix ? (
        <Autocomplete
          options={["Jr", "Sr", "I", "II", "III", "IV"]}
          value={data.suffix || ""}
          onChange={(event, value) => handleAutocompleteChange(event, value, "suffix")}
          renderInput={(params) => (
            <TextField {...params} label="Suffix" fullWidth variant="outlined" />
          )}
        />
      ) : null}
      {nameFields.showAliases ? (
        <TextField
          label={nameFields.aliasesLabel || "Aliases"}
          name="aliases"
          value={data.aliases || ""}
          onChange={handleChange}
          fullWidth
          slotProps={{
            ...(aliasesMaxLength ? { htmlInput: { maxLength: aliasesMaxLength } } : {}),
            ...(showAliasesAka
              ? {
                  input: {
                    startAdornment: (
                      <Box sx={{ mr: 1, color: "text.secondary", whiteSpace: "nowrap" }}>
                        a/k/a
                      </Box>
                    ),
                  },
                }
              : {}),
          }}
        />
      ) : null}
    </>
  );
}
